/**
 * Cal-Giant-specific `calGiant` subfield of a planting's CustomData blob (mirroring
 * the `bonipak` subfield pattern). This is 100% Cal-Giant specific and intentionally
 * isolated from core planting code; it builds on the vendor-agnostic
 * `@/utils/planting-custom-data` plumbing.
 *
 * Plastic color is the first field here. Legacy CG data stored it as a flat top-level
 * `plasticColor` key with messy values (e.g. "", "False", "None", "Black/Clear"); a
 * migration moves those into `calGiant.plasticColor`. Legacy non-canonical values
 * remain *visible* (read the raw value) but are not re-selectable (the dropdown only
 * offers the canonical colors), so reads distinguish "raw for display" from
 * "canonical for selection".
 */

import { parseCustomData, setSubfield } from "@/utils/planting-custom-data";

const CAL_GIANT_KEY = "calGiant";

export const PLASTIC_COLORS = [
	"White",
	"Black",
	"Green",
	"Skunk",
	"Brown",
] as const;
export type PlasticColor = (typeof PLASTIC_COLORS)[number];

/** The Cal-Giant-specific subfield of a planting's CustomData blob. */
export interface CalGiantPlantingData {
	plasticColor?: string;
}

/** Read the whole calGiant subfield (empty object when absent/malformed). */
export function getCalGiantData(
	customData?: string | null,
): CalGiantPlantingData {
	const value = parseCustomData(customData)[CAL_GIANT_KEY];
	return value && typeof value === "object"
		? (value as CalGiantPlantingData)
		: {};
}

/**
 * Merge a partial patch into the calGiant subfield, preserving siblings (e.g. bonipak).
 * Set a field to undefined to remove it. Returns the serialized blob, or undefined
 * when the whole blob becomes empty.
 */
export function setCalGiantData(
	customData: string | null | undefined,
	patch: Partial<CalGiantPlantingData>,
): string | undefined {
	// Overlay the patch, then drop any keys the patch cleared (undefined). Parsed
	// JSON never carries undefined values, so this only removes patch-cleared fields.
	const merged = Object.fromEntries(
		Object.entries({ ...getCalGiantData(customData), ...patch }).filter(
			([, value]) => value !== undefined,
		),
	);
	return setSubfield(customData, CAL_GIANT_KEY, merged);
}

// ---------------------------------------------------------------------------
// Plastic color (field-level semantics)
// ---------------------------------------------------------------------------

/**
 * The raw stored plastic color (any non-empty value, including legacy values like
 * "Black/Clear"), for *display*. Returns undefined when unset/empty/whitespace.
 */
export function getPlasticColorRaw(
	customData?: string | null,
): string | undefined {
	const value = getCalGiantData(customData).plasticColor;
	return value && value.trim() ? value : undefined;
}

/**
 * The plastic color to *pre-select* in the dropdown: only returns a value when the
 * stored value is one of the canonical colors, so legacy junk shows nothing selected.
 */
export function getPlasticColorSelection(
	customData?: string | null,
): PlasticColor | undefined {
	const raw = getPlasticColorRaw(customData);
	return raw && (PLASTIC_COLORS as readonly string[]).includes(raw)
		? (raw as PlasticColor)
		: undefined;
}

/** Set (or clear, when `color` is undefined) the plastic color, preserving siblings. */
export function setPlasticColor(
	customData: string | null | undefined,
	color: PlasticColor | undefined,
): string | undefined {
	return setCalGiantData(customData, { plasticColor: color });
}
